import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';
import { Plus, Edit, Trash2, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', cnpj: '', contact_name: '', phone: '', email: '', address: '',
  city: '', state: '', notes: ''
};

export const SuppliersPage = () => (
  <Layout>
    <SuppliersContent />
  </Layout>
);

export const SuppliersContent = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/suppliers`, { withCredentials: true });
      setSuppliers(data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (editingId) {
        await axios.put(`${API}/suppliers/${editingId}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/suppliers`, formData, { withCredentials: true });
      }
      load();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar fornecedor');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (s) => {
    setFormData({
      name: s.name || '', cnpj: s.cnpj || '', contact_name: s.contact_name || '',
      phone: s.phone || '', email: s.email || '', address: s.address || '',
      city: s.city || '', state: s.state || '', notes: s.notes || ''
    });
    setEditingId(s.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este fornecedor?')) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`, { withCredentials: true });
      load();
    } catch (err) { setError('Erro ao excluir'); }
  };

  const resetForm = () => {
    setFormData(EMPTY);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const filtered = suppliers.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.cnpj && s.cnpj.includes(search))
  );

  return (
    <div className="p-8" data-testid="suppliers-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-black text-slate-900 tracking-tight">Fornecedores</h2>
        <Button onClick={() => setShowForm(!showForm)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-supplier-btn">
          <Plus size={20} className="mr-2" /> Novo Fornecedor
        </Button>
      </div>

        <Input
          placeholder="Buscar por nome ou CNPJ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-6"
        />

        {showForm && (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{editingId ? 'Editar' : 'Novo'} Fornecedor</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Razão Social *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input value={formData.cnpj} onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} placeholder="00.000.000/0000-00" />
              </div>
              <div className="space-y-2">
                <Label>Nome do Contato</Label>
                <Input value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label>Endereço</Label>
                <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Cidade</Label>
                <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} maxLength={2} />
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label>Observações</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              {error && <div className="md:col-span-3"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>}
              <div className="md:col-span-3 flex gap-2">
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">{loading ? 'Salvando...' : 'Salvar'}</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Fornecedor</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">CNPJ</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Contato</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Telefone</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Cidade/UF</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nenhum fornecedor cadastrado</td></tr>}
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Truck size={18} className="text-slate-400" />
                        <span className="font-medium text-slate-900">{s.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{s.cnpj || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{s.contact_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{s.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{s.city ? `${s.city}${s.state ? '/' + s.state : ''}` : '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(s)} className="text-blue-600 hover:text-blue-700"><Edit size={16} /></button>
                        <button onClick={() => handleDelete(s.id)} className="text-red-600 hover:text-red-700"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  );
};
